import React, { createContext, useContext, useState, useEffect } from 'react';
import { Modal } from 'antd';

interface DirtyContextProps {
    isDirty: boolean;
    setDirty: (dirty: boolean) => void;
    confirmNavigation: (next: () => void) => void;
}

const DirtyContext = createContext<DirtyContextProps | undefined>(undefined);

export const DirtyProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [isDirty, setIsDirty] = useState(false);

    useEffect(() => {
        const handleBeforeUnload = (event: BeforeUnloadEvent) => {
            if (isDirty) {
                event.preventDefault();
                event.returnValue = 'Du hast ungespeicherte Änderungen. Seite wirklich verlassen?';
            }
        };
        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [isDirty]);

    const confirmNavigation = (next: () => void) => {
        if (!isDirty) {
            next();
            return;
        }

        Modal.confirm({
            title: 'Nicht gespeicherte Änderungen',
            content: 'Du hast ungespeicherte Änderungen. Seite wirklich verlassen?',
            okText: 'Ja',
            cancelText: 'Nein',
            onOk: () => {
                setIsDirty(false);
                next();
            }
        });
    };

    return (
        <DirtyContext.Provider value={{ isDirty, setDirty: setIsDirty, confirmNavigation }}>
            {children}
        </DirtyContext.Provider>
    );
};

export const useDirtyContext = () => {
    const context = useContext(DirtyContext);
    if (!context) {
        throw new Error('useDirtyContext must be used within a DirtyProvider');
    }
    return context;
};
